import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Plus,
  Edit,
  Trash2,
  ArrowLeft,
  Loader2,
  QrCode,
  MessageSquare,
  Copy,
  Check
} from 'lucide-react';

const DASHBOARD_TYPES = [
  { value: 'entry', label: 'Main Entry', icon: '🚪', defaultLimitEnabled: false },
  { value: 'exit', label: 'Exit', icon: '🚶', defaultLimitEnabled: false },
  { value: 'meal', label: 'Meal/Lunch', icon: '🍽️', defaultLimitEnabled: false },
  { value: 'drinks', label: 'Drinks Counter', icon: '🥤', defaultLimitEnabled: true, defaultLimit: 3 },
  { value: 'kit', label: 'Kit Pickup', icon: '📦', defaultLimitEnabled: false },
  { value: 'gift', label: 'Gift Counter', icon: '🎁', defaultLimitEnabled: false },
  { value: 'tshirt', label: 'T-Shirt Collection', icon: '👕', defaultLimitEnabled: false },
  { value: 'lounge', label: 'VIP Lounge', icon: '✨', defaultLimitEnabled: false },
  { value: 'snacks', label: 'Snacks Counter', icon: '🍿', defaultLimitEnabled: true, defaultLimit: 2 },
  { value: 'photo', label: 'Photo Booth', icon: '📸', defaultLimitEnabled: false },
  { value: 'custom', label: 'Custom', icon: '⚙️', defaultLimitEnabled: false },
  { value: 'vendor', label: 'Vendor (Wallet)', icon: '💰', defaultLimitEnabled: false }
];

const WHATSAPP_TRIGGERS = [
  { value: 'first_scan', label: 'First Scan Only', description: 'Send message only on first check-in' },
  { value: 'every_scan', label: 'Every Scan', description: 'Send message on every check-in' },
  { value: 'disabled', label: 'Disabled', description: 'Do not send any messages' }
];

const DashboardsList = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [categories, setCategories] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // WhatsApp available variables
  const [availableVariables, setAvailableVariables] = useState([]);
  
  // Dialog state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingDashboard, setEditingDashboard] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [credentials, setCredentials] = useState({}); // {dashboard_id: {username, password}}
  
  // New dashboard form
  const [newDashboard, setNewDashboard] = useState({
    dashboard_name: '',
    dashboard_type: 'entry',
    username: '',
    password: '',
    allowed_categories: [],
    category_limits: {},  // Limits per category: {category_id: limit_value}
    whatsapp_enabled: false,
    whatsapp_template_name: '',
    whatsapp_template_language: 'en',
    whatsapp_header_variable: '',
    whatsapp_body_variables: [],
    whatsapp_trigger: 'first_scan',
    // Vendor fields
    menu_items: [],
    max_transaction_amount: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [eventRes, dashboardsRes, categoriesRes] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/dashboards`),
        api.get(`/events/${eventId}/categories`)
      ]);
      setEvent(eventRes.data);
      setDashboards(dashboardsRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
      navigate('/admin/events');
    } finally {
      setLoading(false);
    }
  }, [eventId, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Fetch credentials for all scanners
  const fetchCredentials = useCallback(async () => {
    try {
      const response = await api.get(`/events/${eventId}/dashboards/credentials`);
      const creds = {};
      response.data.dashboards?.forEach(d => {
        // Find the dashboard_id by matching username
        const dashboard = dashboards.find(db => db.username === d.username);
        if (dashboard) {
          creds[dashboard.dashboard_id] = {
            username: d.username,
            password: d.password
          };
        }
      });
      setCredentials(creds);
    } catch (error) {
      // Intentionally not logging the full error object to avoid leaking sensitive data
    }
  }, [eventId, dashboards]);

  // Fetch credentials when dashboards are loaded
  useEffect(() => {
    if (dashboards.length > 0) {
      fetchCredentials();
    }
  }, [dashboards, fetchCredentials]);

  const fetchWhatsAppTemplates = async () => {
    try {
      const response = await api.get('/interakt/templates');
      setAvailableVariables(response.data.available_variables || []);
    } catch (error) {
      // Fallback to hardcoded variables
      setAvailableVariables([
        { key: 'name', label: 'Full Name' },
        { key: 'call_name', label: 'Call Name' },
        { key: 'ticket_id', label: 'Ticket ID' },
        { key: 'category', label: 'Category' },
        { key: 'event_name', label: 'Event Name' },
        { key: 'booth_name', label: 'Booth Name' },
        { key: 'tshirt_size', label: 'T-Shirt Size' },
        { key: 'scan_time', label: 'Scan Time' },
        { key: 'scan_count', label: 'Scan Count' },
        { key: 'phone_number', label: 'Phone Number' },
        { key: 'email', label: 'Email' },
        { key: 'club_name', label: 'Club Name' },
      ]);
    }
  };

  const handleCreateDashboard = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const payload = { ...newDashboard };
      if (payload.max_transaction_amount === '' || payload.max_transaction_amount === null) {
        payload.max_transaction_amount = null;
      }
      if (payload.menu_items) {
        payload.menu_items = payload.menu_items.map(item => ({
          ...item,
          price: item.price === '' ? 0 : parseFloat(item.price) || 0
        }));
      }
      await api.post(`/events/${eventId}/dashboards`, payload);
      toast.success('Scanner dashboard created!');
      setCreateDialogOpen(false);
      setNewDashboard({
        dashboard_name: '',
        dashboard_type: 'entry',
        username: '',
        password: '',
        allowed_categories: [],
        category_limits: {},
        whatsapp_enabled: false,
        whatsapp_template_name: '',
        whatsapp_template_language: 'en',
        whatsapp_header_variable: '',
        whatsapp_body_variables: [],
        whatsapp_trigger: 'first_scan',
        menu_items: [],
        max_transaction_amount: ''
      });
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Failed to create dashboard');
      toast.error(message);
    } finally {
      setCreating(false);
    }
  };

  const handleUpdateDashboard = async () => {
    try {
      const updatePayload = {
        dashboard_name: editingDashboard.dashboard_name,
        dashboard_type: editingDashboard.dashboard_type,
        username: editingDashboard.username,
        allowed_categories: editingDashboard.allowed_categories,
        category_limits: editingDashboard.category_limits || {},
        status: editingDashboard.status,
        whatsapp_enabled: editingDashboard.whatsapp_enabled,
        whatsapp_template_name: editingDashboard.whatsapp_template_name,
        whatsapp_template_language: editingDashboard.whatsapp_template_language,
        whatsapp_header_variable: editingDashboard.whatsapp_header_variable,
        whatsapp_body_variables: editingDashboard.whatsapp_body_variables,
        whatsapp_trigger: editingDashboard.whatsapp_trigger
      };

      // Include vendor fields when dashboard type is vendor
      if (editingDashboard.dashboard_type === 'vendor') {
        updatePayload.menu_items = (editingDashboard.menu_items || []).map(item => ({
          ...item,
          price: item.price === '' ? 0 : parseFloat(item.price) || 0
        }));
        const rawMax = editingDashboard.max_transaction_amount;
        updatePayload.max_transaction_amount = (rawMax === '' || rawMax == null)
          ? null
          : parseFloat(rawMax) || null;
      }

      // Only include password if a new one was entered
      if (editingDashboard.new_password && editingDashboard.new_password.trim()) {
        updatePayload.password = editingDashboard.new_password;
      }
      
      await api.put(`/events/${eventId}/dashboards/${editingDashboard.dashboard_id}`, updatePayload);
      toast.success('Dashboard updated');
      setEditDialogOpen(false);
      setEditingDashboard(null);
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : 'Failed to update dashboard');
      toast.error(message);
    }
  };

  const handleDeleteDashboard = async (dashboardId, dashboardName) => {
    if (!window.confirm(`Delete scanner "${dashboardName}"?`)) return;
    
    try {
      await api.delete(`/events/${eventId}/dashboards/${dashboardId}`);
      toast.success('Dashboard deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete dashboard');
    }
  };

  const copyCredentials = (dashboard) => {
    // Use event-specific scanner URL with slug
    const eventScannerUrl = `${window.location.origin}/scanner/${event?.event_url_slug || eventId}/login`;
    const text = `Scanner URL: ${eventScannerUrl}\nUsername: ${dashboard.username}\nPassword: ${dashboard.password_display || 'Please reset password in Edit'}\n\nShare these credentials with your scanner operator.`;
    navigator.clipboard.writeText(text);
    setCopiedId(dashboard.dashboard_id);
    setTimeout(() => setCopiedId(null), 2000);
    toast.success('Credentials copied!');
  };

  const handleCategoryToggle = (categoryId, isNew = false) => {
    if (isNew) {
      const updated = newDashboard.allowed_categories.includes(categoryId)
        ? newDashboard.allowed_categories.filter(id => id !== categoryId)
        : [...newDashboard.allowed_categories, categoryId];
      setNewDashboard({...newDashboard, allowed_categories: updated});
    } else if (editingDashboard) {
      const updated = editingDashboard.allowed_categories.includes(categoryId)
        ? editingDashboard.allowed_categories.filter(id => id !== categoryId)
        : [...editingDashboard.allowed_categories, categoryId];
      setEditingDashboard({...editingDashboard, allowed_categories: updated});
    }
  };

  const getTypeInfo = (type) => DASHBOARD_TYPES.find(t => t.value === type) || DASHBOARD_TYPES[7];

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/events/${eventId}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Scanner Dashboards</h1>
          <p className="text-gray-500">{event?.event_name}</p>
        </div>
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-dashboard-button">
              <Plus className="h-4 w-4 mr-2" /> Create Scanner
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Create Scanner Dashboard</DialogTitle>
              <DialogDescription>Set up a new scanner station for your event</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateDashboard} className="space-y-4">
              <div className="space-y-2">
                <Label>Dashboard Name *</Label>
                <Input
                  value={newDashboard.dashboard_name}
                  onChange={(e) => setNewDashboard({...newDashboard, dashboard_name: e.target.value})}
                  placeholder="Main Entry Scanner"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label>Type *</Label>
                <Select
                  value={newDashboard.dashboard_type}
                  onValueChange={(v) => {
                    setNewDashboard({
                      ...newDashboard,
                      dashboard_type: v
                    });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DASHBOARD_TYPES
                      .filter((type) => type.value !== 'vendor' || event?.wallet_enabled)
                      .map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Username *</Label>
                  <Input
                    value={newDashboard.username}
                    onChange={(e) => setNewDashboard({...newDashboard, username: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                    placeholder="entry_scanner"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Password *</Label>
                  <Input
                    type="password"
                    value={newDashboard.password}
                    onChange={(e) => setNewDashboard({...newDashboard, password: e.target.value})}
                    required
                    minLength={4}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Allowed Categories</Label>
                <p className="text-sm text-gray-500">Leave empty to allow all categories</p>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {categories.map((cat) => (
                    <div key={cat.category_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`cat-${cat.category_id}`}
                        checked={newDashboard.allowed_categories.includes(cat.category_id)}
                        onCheckedChange={() => handleCategoryToggle(cat.category_id, true)}
                      />
                      <Label htmlFor={`cat-${cat.category_id}`} className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.category_color }} />
                        {cat.category_name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Category Scan Limits Section - hidden for vendor dashboards */}
              {newDashboard.dashboard_type !== 'vendor' && (
                <div className="space-y-2 border-t pt-4">
                  <Label className="text-base font-medium">🎯 Scan Limits per Category</Label>
                  <p className="text-sm text-gray-500">Set max scans allowed at this scanner for each category. Leave empty for unlimited.</p>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {categories.map((cat) => (
                      <div key={cat.category_id} className="flex items-center justify-between gap-3 p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 flex-1">
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.category_color }} />
                          <span className="text-sm font-medium">{cat.category_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="9999"
                            placeholder="∞"
                            className="w-20 h-8 text-sm text-center"
                            value={newDashboard.category_limits?.[cat.category_id] || ''}
                            onChange={(e) => {
                              let val = e.target.value;
                              // Limit to max 9999
                              if (val !== '' && parseInt(val) > 9999) {
                                val = '9999';
                              }
                              setNewDashboard({
                                ...newDashboard,
                                category_limits: {
                                  ...newDashboard.category_limits,
                                  [cat.category_id]: val === '' ? null : parseInt(val)
                                }
                              });
                            }}
                          />
                          <span className="text-xs text-gray-500 w-12">scans</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Vendor-specific fields */}
              {newDashboard.dashboard_type === 'vendor' && (
                <div className="border rounded-lg p-4 space-y-4">
                  <div>
                    <Label className="text-base font-medium">Vendor Settings</Label>
                    <p className="text-sm text-gray-500">Configure vendor-specific options</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Max Transaction Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={newDashboard.max_transaction_amount}
                      onChange={(e) => setNewDashboard({...newDashboard, max_transaction_amount: e.target.value ? parseFloat(e.target.value) : ''})}
                      placeholder="Leave empty for no limit"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Menu Items</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setNewDashboard({
                          ...newDashboard,
                          menu_items: [...newDashboard.menu_items, { name: '', price: '', category: '', active: true }]
                        })}
                      >
                        <Plus className="h-3 w-3 mr-1" /> Add Item
                      </Button>
                    </div>
                    {newDashboard.menu_items.map((item, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <Input
                          placeholder="Item name"
                          value={item.name}
                          onChange={(e) => {
                            const items = [...newDashboard.menu_items];
                            items[idx] = {...items[idx], name: e.target.value};
                            setNewDashboard({...newDashboard, menu_items: items});
                          }}
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          placeholder="Price"
                          value={item.price === '' ? '' : item.price}
                          onChange={(e) => {
                            const items = [...newDashboard.menu_items];
                            items[idx] = {...items[idx], price: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)};
                            setNewDashboard({...newDashboard, menu_items: items});
                          }}
                          className="w-24"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const items = newDashboard.menu_items.filter((_, i) => i !== idx);
                            setNewDashboard({...newDashboard, menu_items: items});
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base">📱 Auto WhatsApp on Scan</Label>
                    <p className="text-sm text-gray-500">Send message when attendee is scanned</p>
                  </div>
                  <Switch
                    checked={newDashboard.whatsapp_enabled}
                    onCheckedChange={(v) => {
                      setNewDashboard({...newDashboard, whatsapp_enabled: v});
                      if (v && availableVariables.length === 0) {
                        fetchWhatsAppTemplates();
                      }
                    }}
                  />
                </div>
                
                {newDashboard.whatsapp_enabled && (
                  <>
                    <div className="space-y-2">
                      <Label>Send Message When</Label>
                      <Select
                        value={newDashboard.whatsapp_trigger}
                        onValueChange={(v) => setNewDashboard({...newDashboard, whatsapp_trigger: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WHATSAPP_TRIGGERS.map((trigger) => (
                            <SelectItem key={trigger.value} value={trigger.value}>
                              {trigger.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-gray-500">
                        {WHATSAPP_TRIGGERS.find(t => t.value === newDashboard.whatsapp_trigger)?.description}
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Interakt Template Name *</Label>
                      <Input
                        value={newDashboard.whatsapp_template_name}
                        onChange={(e) => setNewDashboard({...newDashboard, whatsapp_template_name: e.target.value})}
                        placeholder="e.g., kit_pickup_notification"
                      />
                      <p className="text-xs text-gray-500">
                        Enter the exact template name from your Interakt dashboard (Settings → Templates)
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Template Language Code</Label>
                      <Input
                        value={newDashboard.whatsapp_template_language}
                        onChange={(e) => setNewDashboard({...newDashboard, whatsapp_template_language: e.target.value})}
                        placeholder="en"
                      />
                      <p className="text-xs text-gray-500">Language code (e.g., en, hi, es)</p>
                    </div>
                    
                    <div className="space-y-3 border-t pt-3">
                      <Label className="text-sm font-medium">Map Template Variables</Label>
                      <p className="text-xs text-gray-500">
                        If your template has variables like {"{{1}}"}, {"{{2}}"}, etc., map them to attendee data below:
                      </p>
                      
                      {[1, 2, 3, 4, 5].map((varNum) => (
                        <div key={varNum} className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-12">{`{{${varNum}}}`}</span>
                          <Select
                            value={newDashboard.whatsapp_body_variables[varNum - 1] || '_none_'}
                            onValueChange={(v) => {
                              const vars = [...(newDashboard.whatsapp_body_variables || [])];
                              vars[varNum - 1] = v === '_none_' ? '' : v;
                              setNewDashboard({...newDashboard, whatsapp_body_variables: vars});
                            }}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="(not used)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none_">(not used)</SelectItem>
                              {availableVariables.map((v) => (
                                <SelectItem key={v.key} value={v.key}>
                                  {v.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                    
                    <div className="bg-blue-50 p-3 rounded text-xs">
                      <p className="font-medium text-blue-700 mb-1">Available Attendee Fields:</p>
                      <p className="text-blue-600">
                        {availableVariables.length > 0 
                          ? availableVariables.map(v => v.label).join(', ')
                          : 'Full Name, Call Name, Ticket ID, Category, Event Name, Booth Name, T-Shirt Size, Scan Time, Scan Count, Phone, Email, Club'}
                      </p>
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={creating}>
                  {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating...</> : 'Create Scanner'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dashboards Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : dashboards.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <QrCode className="h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No scanner dashboards yet</h3>
            <p className="text-gray-500 mb-4">Create scanner dashboards for different check-in points</p>
            <Button onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" /> Create Scanner
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {dashboards.map((dashboard) => {
            const typeInfo = getTypeInfo(dashboard.dashboard_type);
            return (
              <Card key={dashboard.dashboard_id} className="overflow-hidden" data-testid={`dashboard-card-${dashboard.dashboard_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{typeInfo.icon}</span>
                      <div>
                        <CardTitle className="text-lg">{dashboard.dashboard_name}</CardTitle>
                        <p className="text-sm text-gray-500">{typeInfo.label}</p>
                      </div>
                    </div>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      dashboard.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                    }`}>
                      {dashboard.status}
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Username</span>
                      <span className="font-mono font-medium">{dashboard.username}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Password</span>
                      <span className="font-mono font-medium">
                        {dashboard.password_display || '••••••••'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Total Scans</span>
                      <span className="font-medium">{dashboard.total_scans || 0}</span>
                    </div>
                    {dashboard.dashboard_type === 'vendor' && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Total Sales</span>
                        <span className="font-medium text-green-600">
                          {new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(dashboard.total_sales || 0)}
                        </span>
                      </div>
                    )}
                  </div>

                  {dashboard.dashboard_type === 'vendor' && (
                    <div className="flex items-center gap-2 text-sm text-purple-600 bg-purple-50 p-2 rounded">
                      <span>💰</span>
                      <span>Vendor Dashboard — {dashboard.menu_items?.length || 0} menu items</span>
                    </div>
                  )}

                  {dashboard.whatsapp_enabled && (
                    <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 p-2 rounded">
                      <MessageSquare className="h-4 w-4" />
                      <span>
                        WhatsApp: {dashboard.whatsapp_trigger === 'every_scan' ? 'Every scan' : 
                                   dashboard.whatsapp_trigger === 'first_scan' ? 'First scan only' : 
                                   'Disabled'}
                      </span>
                    </div>
                  )}
                  
                  {dashboard.allowed_categories?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {dashboard.allowed_categories.map((catId) => {
                        const cat = categories.find(c => c.category_id === catId);
                        return cat ? (
                          <span
                            key={catId}
                            className="px-2 py-0.5 text-xs rounded-full text-white"
                            style={{ backgroundColor: cat.category_color }}
                          >
                            {cat.category_name}
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                  
                  {/* Show category limits if any are set */}
                  {dashboard.category_limits && Object.keys(dashboard.category_limits).filter(k => dashboard.category_limits[k] !== null).length > 0 && (
                    <div className="bg-orange-50 p-2 rounded text-xs">
                      <span className="font-medium text-orange-700">Scan Limits: </span>
                      <span className="text-orange-600">
                        {Object.entries(dashboard.category_limits)
                          .filter(([_, v]) => v !== null)
                          .map(([catId, limit]) => {
                            const cat = categories.find(c => c.category_id === catId);
                            return cat ? `${cat.category_name}: ${limit}` : null;
                          })
                          .filter(Boolean)
                          .join(', ')}
                      </span>
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => copyCredentials(dashboard)}
                    >
                      {copiedId === dashboard.dashboard_id ? (
                        <><Check className="h-4 w-4 mr-1" /> Copied!</>
                      ) : (
                        <><Copy className="h-4 w-4 mr-1" /> Copy Login</>
                      )}
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { 
                        const cred = credentials[dashboard.dashboard_id];
                        setEditingDashboard({
                          ...dashboard, 
                          current_password: cred?.password || '',
                          new_password: ''
                        }); 
                        setEditDialogOpen(true); 
                      }}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => handleDeleteDashboard(dashboard.dashboard_id, dashboard.dashboard_name)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Scanner Dashboard</DialogTitle>
          </DialogHeader>
          {editingDashboard && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Dashboard Name</Label>
                <Input
                  value={editingDashboard.dashboard_name}
                  onChange={(e) => setEditingDashboard({...editingDashboard, dashboard_name: e.target.value})}
                />
              </div>
              
              {/* Login Credentials */}
              <div className="border rounded-lg p-4 space-y-4 bg-gray-50">
                <Label className="text-base font-medium">🔐 Login Credentials</Label>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Username</Label>
                    <Input
                      value={editingDashboard.username || ''}
                      onChange={(e) => setEditingDashboard({...editingDashboard, username: e.target.value})}
                      placeholder="Scanner username"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Current Password</Label>
                    <Input
                      value={editingDashboard.current_password || ''}
                      readOnly
                      className="bg-gray-100"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>New Password (optional)</Label>
                  <Input
                    type="text"
                    value={editingDashboard.new_password || ''}
                    onChange={(e) => setEditingDashboard({...editingDashboard, new_password: e.target.value})}
                    placeholder="Enter new password to change"
                  />
                  <p className="text-xs text-gray-500">Leave blank to keep the current password.</p>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select
                    value={editingDashboard.dashboard_type}
                    onValueChange={(v) => setEditingDashboard({...editingDashboard, dashboard_type: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DASHBOARD_TYPES
                        .filter((type) => type.value !== 'vendor' || event?.wallet_enabled)
                        .map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.icon} {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={editingDashboard.status}
                    onValueChange={(v) => setEditingDashboard({...editingDashboard, status: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Allowed Categories</Label>
                <div className="grid grid-cols-2 gap-2">
                  {categories.map((cat) => (
                    <div key={cat.category_id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-cat-${cat.category_id}`}
                        checked={editingDashboard.allowed_categories?.includes(cat.category_id)}
                        onCheckedChange={() => handleCategoryToggle(cat.category_id, false)}
                      />
                      <Label htmlFor={`edit-cat-${cat.category_id}`}>
                        {cat.category_name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Category Scan Limits Section - hidden for vendor dashboards */}
              {editingDashboard.dashboard_type !== 'vendor' && (
                <div className="space-y-2 border-t pt-4">
                  <Label className="text-base font-medium">🎯 Scan Limits per Category</Label>
                  <p className="text-sm text-gray-500">Set max scans at this scanner for each category. Leave empty for unlimited.</p>
                  <div className="grid grid-cols-1 gap-2 mt-2">
                    {categories.map((cat) => (
                      <div key={cat.category_id} className="flex items-center justify-between gap-3 p-2 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-2 flex-1">
                          <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: cat.category_color }} />
                          <span className="text-sm font-medium">{cat.category_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            min="1"
                            max="9999"
                            placeholder="∞"
                            className="w-20 h-8 text-sm text-center"
                            value={editingDashboard.category_limits?.[cat.category_id] || ''}
                            onChange={(e) => {
                              let val = e.target.value;
                              // Limit to max 9999
                              if (val !== '' && parseInt(val) > 9999) {
                                val = '9999';
                              }
                              setEditingDashboard({
                                ...editingDashboard,
                                category_limits: {
                                  ...(editingDashboard.category_limits || {}),
                                  [cat.category_id]: val === '' ? null : parseInt(val)
                                }
                              });
                            }}
                          />
                          <span className="text-xs text-gray-500 w-12">scans</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Vendor-specific fields */}
              {editingDashboard.dashboard_type === 'vendor' && (
                <div className="border rounded-lg p-4 space-y-4">
                  <div>
                    <Label className="text-base font-medium">Vendor Settings</Label>
                    <p className="text-sm text-gray-500">Configure vendor-specific options</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Max Transaction Amount</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={editingDashboard.max_transaction_amount || ''}
                      onChange={(e) => setEditingDashboard({...editingDashboard, max_transaction_amount: e.target.value ? parseFloat(e.target.value) : ''})}
                      placeholder="Leave empty for no limit"
                    />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Menu Items</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setEditingDashboard({
                          ...editingDashboard,
                          menu_items: [...(editingDashboard.menu_items || []), { name: '', price: '', category: '', active: true }]
                        })}
                      >
                        <Plus className="h-3 w-3 mr-1" /> Add Item
                      </Button>
                    </div>
                    {(editingDashboard.menu_items || []).map((item, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <Input
                          placeholder="Item name"
                          value={item.name}
                          onChange={(e) => {
                            const items = [...(editingDashboard.menu_items || [])];
                            items[idx] = {...items[idx], name: e.target.value};
                            setEditingDashboard({...editingDashboard, menu_items: items});
                          }}
                          className="flex-1"
                        />
                        <Input
                          type="number"
                          placeholder="Price"
                          value={item.price === '' ? '' : item.price}
                          onChange={(e) => {
                            const items = [...(editingDashboard.menu_items || [])];
                            items[idx] = {...items[idx], price: e.target.value === '' ? '' : (parseFloat(e.target.value) || 0)};
                            setEditingDashboard({...editingDashboard, menu_items: items});
                          }}
                          className="w-24"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            const items = (editingDashboard.menu_items || []).filter((_, i) => i !== idx);
                            setEditingDashboard({...editingDashboard, menu_items: items});
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="font-medium">📱 Auto WhatsApp on Scan</Label>
                    <p className="text-sm text-gray-500">Send message when attendee is scanned</p>
                  </div>
                  <Switch
                    checked={editingDashboard.whatsapp_enabled}
                    onCheckedChange={(v) => {
                      setEditingDashboard({...editingDashboard, whatsapp_enabled: v});
                      if (v && availableVariables.length === 0) {
                        fetchWhatsAppTemplates();
                      }
                    }}
                  />
                </div>
                
                {editingDashboard.whatsapp_enabled && (
                  <>
                    <div className="space-y-2">
                      <Label>Send Message When</Label>
                      <Select
                        value={editingDashboard.whatsapp_trigger || 'first_scan'}
                        onValueChange={(v) => setEditingDashboard({...editingDashboard, whatsapp_trigger: v})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {WHATSAPP_TRIGGERS.map((trigger) => (
                            <SelectItem key={trigger.value} value={trigger.value}>
                              {trigger.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Interakt Template Name *</Label>
                      <Input
                        value={editingDashboard.whatsapp_template_name || ''}
                        onChange={(e) => setEditingDashboard({...editingDashboard, whatsapp_template_name: e.target.value})}
                        placeholder="e.g., kit_pickup_notification"
                      />
                      <p className="text-xs text-gray-500">
                        Enter the exact template name from your Interakt dashboard
                      </p>
                    </div>
                    
                    <div className="space-y-2">
                      <Label>Language Code</Label>
                      <Input
                        value={editingDashboard.whatsapp_template_language || 'en'}
                        onChange={(e) => setEditingDashboard({...editingDashboard, whatsapp_template_language: e.target.value})}
                        placeholder="en"
                      />
                    </div>
                    
                    <div className="space-y-2 border-t pt-2">
                      <Label className="text-sm">Variable Mapping</Label>
                      {[1, 2, 3, 4, 5].map((varNum) => (
                        <div key={varNum} className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-12">{`{{${varNum}}}`}</span>
                          <Select
                            value={(editingDashboard.whatsapp_body_variables || [])[varNum - 1] || '_none_'}
                            onValueChange={(v) => {
                              const vars = [...(editingDashboard.whatsapp_body_variables || [])];
                              vars[varNum - 1] = v === '_none_' ? '' : v;
                              setEditingDashboard({...editingDashboard, whatsapp_body_variables: vars});
                            }}
                          >
                            <SelectTrigger className="h-8 text-sm">
                              <SelectValue placeholder="(not used)" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="_none_">(not used)</SelectItem>
                              {availableVariables.map((v) => (
                                <SelectItem key={v.key} value={v.key}>
                                  {v.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleUpdateDashboard}>Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default DashboardsList;
